import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

// Clarias brand orange
const BRAND = [234, 88, 12];

// jsPDF's built-in fonts only support WinAnsi/Latin-1. Map the common
// non-Latin-1 characters that appear in UPSC content (rupee, smart quotes,
// dashes, bullets) to safe equivalents and drop anything else, otherwise the
// whole text run renders as garbage.
function clean(s) {
  if (s === null || s === undefined) return "";
  return String(s)
    .replace(/\u20B9/g, "Rs ")
    .replace(/[\u2018\u2019\u201A\u2032]/g, "'")
    .replace(/[\u201C\u201D\u201E\u2033]/g, '"')
    .replace(/[\u2013\u2014\u2212]/g, "-")
    .replace(/\u2026/g, "...")
    .replace(/[\u2022\u25CF\u25AA\u25E6\u2043\u00B7]/g, "-")
    .replace(/\u00A0/g, " ")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/[^\x00-\x7F]/g, "");
}

/**
 * Lightweight, text-based PDF builder for Clarias model answers/essays.
 * Produces crisp, selectable text (not a screenshot) with a branded header,
 * page numbers and a study-use footer. Diagrams are rasterized and embedded.
 */
class PdfBuilder {
  constructor(logo = null) {
    this.doc = new jsPDF({ unit: "mm", format: "a4" });
    this.pageW = 210;
    this.pageH = 297;
    this.margin = 15;
    this.contentW = this.pageW - this.margin * 2;
    this.y = 0;
    this.logo = logo;
    this._header();
  }

  _header() {
    const d = this.doc;
    d.setFillColor(255, 255, 255);
    d.rect(0, 0, this.pageW, 18, "F");
    let x = this.margin;
    if (this.logo) {
      const h = 10;
      const w = h * (this.logo.w / this.logo.h);
      d.addImage(this.logo.dataUrl, "PNG", x, 4, w, h);
      x += w + 2;
    }
    // lowercase serif wordmark, deep navy
    d.setFont("times", "bold");
    d.setFontSize(18);
    d.setTextColor(11, 18, 32);
    d.text("clarias", x, 12.8);
    d.setFont("helvetica", "normal");
    d.setFontSize(8.5);
    d.setTextColor(120, 120, 130);
    d.text("Model Answer  •  clarias.in", this.pageW - this.margin, 11.5, { align: "right" });
    // orange accent rule under the header
    d.setDrawColor(...BRAND);
    d.setLineWidth(0.7);
    d.line(0, 18, this.pageW, 18);
    d.setLineWidth(0.2);
    this.y = 26;
    d.setTextColor(30, 30, 30);
  }

  _ensure(h) {
    if (this.y + h > this.pageH - 16) {
      this.doc.addPage();
      this._header();
    }
  }

  title(text) {
    if (!text) return;
    const d = this.doc;
    d.setFont("helvetica", "bold");
    d.setFontSize(15);
    d.setTextColor(20, 20, 35);
    d.splitTextToSize(clean(text), this.contentW).forEach((l) => {
      this._ensure(7);
      d.text(l, this.margin, this.y);
      this.y += 7;
    });
    this.y += 1;
  }

  metaLine(text) {
    if (!text) return;
    const d = this.doc;
    d.setFont("helvetica", "normal");
    d.setFontSize(9);
    d.setTextColor(120, 120, 130);
    this._ensure(5);
    d.text(clean(text), this.margin, this.y);
    this.y += 5;
  }

  divider() {
    const d = this.doc;
    this._ensure(4);
    d.setDrawColor(225, 225, 232);
    d.line(this.margin, this.y, this.pageW - this.margin, this.y);
    this.y += 4;
  }

  sectionHeading(text) {
    const d = this.doc;
    this._ensure(12);
    this.y += 3;
    d.setFillColor(...BRAND);
    d.rect(this.margin, this.y - 4, 1.6, 5.5, "F");
    d.setFont("helvetica", "bold");
    d.setFontSize(12);
    d.setTextColor(...BRAND);
    d.text(clean(text).toUpperCase(), this.margin + 4, this.y);
    this.y += 6;
  }

  subHeading(text, color = [55, 55, 65]) {
    if (!text) return;
    const d = this.doc;
    d.setFont("helvetica", "bold");
    d.setFontSize(10.5);
    d.setTextColor(...color);
    d.splitTextToSize(clean(text), this.contentW).forEach((l) => {
      this._ensure(5);
      d.text(l, this.margin, this.y);
      this.y += 5;
    });
    this.y += 0.5;
  }

  paragraph(text, opts = {}) {
    if (!text) return;
    const d = this.doc;
    d.setFont("helvetica", opts.italic ? "italic" : "normal");
    const size = opts.size || 10.5;
    d.setFontSize(size);
    d.setTextColor(...(opts.color || [45, 45, 50]));
    const lh = size * 0.42;
    d.splitTextToSize(clean(text), this.contentW).forEach((line) => {
      this._ensure(lh + 1);
      d.text(line, this.margin, this.y);
      this.y += lh + 1.2;
    });
    this.y += 1.5;
  }

  list(items, ordered = false) {
    if (!items || !items.length) return;
    const d = this.doc;
    d.setFont("helvetica", "normal");
    d.setFontSize(10);
    d.setTextColor(45, 45, 50);
    const indent = 6;
    items.forEach((item, i) => {
      const marker = ordered ? `${i + 1}.` : "•";
      d.splitTextToSize(clean(item), this.contentW - indent).forEach((line, li) => {
        this._ensure(5);
        if (li === 0) d.text(marker, this.margin, this.y);
        d.text(line, this.margin + indent, this.y);
        this.y += 4.6;
      });
      this.y += 1;
    });
    this.y += 1;
  }

  labeledBox(label, text, color = [235, 244, 255]) {
    if (!text) return;
    const d = this.doc;
    d.setFontSize(9);
    const bodyLines = d.splitTextToSize(clean(text), this.contentW - 8);
    const boxH = 7 + bodyLines.length * 4 + 2;
    this._ensure(boxH + 2);
    d.setFillColor(...color);
    d.roundedRect(this.margin, this.y - 1, this.contentW, boxH, 1.5, 1.5, "F");
    d.setFont("helvetica", "bold");
    d.setFontSize(8);
    d.setTextColor(70, 70, 80);
    d.text(clean(label).toUpperCase(), this.margin + 3, this.y + 3.5);
    d.setFont("helvetica", "normal");
    d.setFontSize(9);
    d.setTextColor(55, 55, 60);
    let yy = this.y + 8;
    bodyLines.forEach((l) => {
      d.text(l, this.margin + 3, yy);
      yy += 4;
    });
    this.y += boxH + 2.5;
  }

  mono(text) {
    if (!text) return;
    const d = this.doc;
    d.setFont("courier", "normal");
    d.setFontSize(8);
    d.setTextColor(60, 60, 65);
    clean(text).split("\n").forEach((line) => {
      d.splitTextToSize(line, this.contentW).forEach((l) => {
        this._ensure(4);
        d.text(l, this.margin, this.y);
        this.y += 4;
      });
    });
    this.y += 2;
    d.setFont("helvetica", "normal");
  }

  image(dataUrl, imgW, imgH) {
    const ratio = imgH / imgW;
    let w = this.contentW;
    let h = w * ratio;
    const maxH = this.pageH - 50;
    if (h > maxH) {
      h = maxH;
      w = h / ratio;
    }
    this._ensure(h + 4);
    this.doc.addImage(dataUrl, "PNG", this.margin + (this.contentW - w) / 2, this.y, w, h);
    this.y += h + 4;
  }

  save(filename) {
    const d = this.doc;
    const total = d.getNumberOfPages();
    for (let p = 1; p <= total; p++) {
      d.setPage(p);
      d.setFont("helvetica", "normal");
      d.setFontSize(8);
      d.setTextColor(155, 155, 162);
      d.text("Generated by clarias • clarias.in — for personal study use", this.margin, this.pageH - 8);
      d.text(`Page ${p} of ${total}`, this.pageW - this.margin, this.pageH - 8, { align: "right" });
    }
    // Manual blob download — more reliable across browsers/sandboxes than doc.save().
    const blob = d.output("blob");
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 1500);
  }
}

async function captureElement(elementId) {
  const el = document.getElementById(elementId);
  if (!el) return null;
  try {
    // Hard timeout so a html2canvas hang (e.g. on unsupported CSS color funcs)
    // can never block the rest of the PDF — we just skip the diagram.
    const canvas = await Promise.race([
      html2canvas(el, { scale: 1.5, backgroundColor: "#ffffff", logging: false, useCORS: true }),
      new Promise((_, reject) => setTimeout(() => reject(new Error("diagram capture timeout")), 6000)),
    ]);
    return { dataUrl: canvas.toDataURL("image/png"), w: canvas.width, h: canvas.height };
  } catch (e) {
    console.warn("Diagram capture skipped:", e?.message || e);
    return null;
  }
}

function safeName(s) {
  return String(s || "answer")
    .replace(/[^a-z0-9]+/gi, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 50);
}

// Brand flame mark used in the PDF header. Loaded once per download.
const CLARIAS_LOGO_SRC = "/clarias-logo-nobg.png";
function loadLogo() {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const c = document.createElement("canvas");
          c.width = img.naturalWidth;
          c.height = img.naturalHeight;
          c.getContext("2d").drawImage(img, 0, 0);
          resolve({ dataUrl: c.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight });
        } catch (e) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = CLARIAS_LOGO_SRC;
    } catch (e) {
      resolve(null);
    }
  });
}

/** Build + download a PDF for a GS Mains model answer. */
export async function downloadMainsAnswerPdf({ suite, question, marks, wordLimit, answer, diagramElementId, diagramLabel }) {
  const logo = await loadLogo();
  const b = new PdfBuilder(logo);
  b.title(question || "UPSC Mains — Model Answer");
  b.metaLine(`${suite || "GS"}  •  ${marks || 10} marks  •  ~${wordLimit || 150} words`);
  b.divider();

  if (answer.interpretation) {
    b.sectionHeading("Question Interpretation");
    b.paragraph(answer.interpretation);
  }
  if (answer.intro) {
    b.sectionHeading("Introduction");
    b.paragraph(answer.intro);
  }
  if (diagramElementId) {
    const cap = await captureElement(diagramElementId);
    if (cap) {
      b.sectionHeading(`Diagram${diagramLabel ? ` — ${diagramLabel}` : ""}`);
      b.image(cap.dataUrl, cap.w, cap.h);
      if (answer.diagram?.description) {
        b.paragraph(answer.diagram.description, { italic: true, size: 9, color: [110, 110, 115] });
      }
    }
  }
  if (answer.body?.length) {
    b.sectionHeading("Body");
    answer.body.forEach((section, idx) => {
      b.subHeading(`Part ${String.fromCharCode(65 + idx)}: ${section.heading || ""}`, [90, 45, 130]);
      (section.points || []).forEach((pt) => {
        if (pt.keyword) b.subHeading(pt.keyword, [30, 30, 35]);
        if (pt.explanation) b.paragraph(pt.explanation);
        if (pt.example) b.labeledBox("Example", pt.example, [235, 244, 255]);
        if (pt.stat) b.labeledBox("Data", pt.stat, [235, 250, 240]);
      });
    });
  }
  if (answer.way_forward?.length) {
    b.sectionHeading("Way Forward");
    b.list(answer.way_forward, true);
  }
  if (answer.conclusion) {
    b.sectionHeading("Conclusion");
    b.paragraph(answer.conclusion);
  }
  if (answer.alternates?.intros?.length) {
    b.sectionHeading("Alternative Introductions");
    b.list(answer.alternates.intros, true);
  }
  if (answer.alternates?.conclusions?.length) {
    b.sectionHeading("Alternative Conclusions");
    b.list(answer.alternates.conclusions, true);
  }

  b.save(`Clarias_${suite || "GS"}_${safeName(question)}.pdf`);
}

/** Build + download a PDF for a Mains Essay model. */
export async function downloadEssayPdf({ topic, essay }) {
  const logo = await loadLogo();
  const b = new PdfBuilder(logo);
  b.title(topic || "UPSC Essay — Model");
  b.metaLine("Essay  •  Clarias Model Essay");
  b.divider();

  if (essay.deconstruction) {
    b.sectionHeading("Topic Deconstruction");
    if (essay.deconstruction.keywords?.length) b.paragraph("Keywords: " + essay.deconstruction.keywords.join(", "));
    if (essay.deconstruction.dimensions?.length) b.paragraph("Dimensions: " + essay.deconstruction.dimensions.join(", "));
  }
  if (essay.outline?.length) {
    b.sectionHeading("Essay Outline");
    essay.outline.forEach((sec) => {
      b.subHeading(sec.subheading || "");
      if (sec.points?.length) b.list(sec.points);
    });
  }
  if (essay.introductions) {
    b.sectionHeading("Introduction Styles");
    if (essay.introductions.data_based) { b.subHeading("Data/Report-Based", [60, 60, 140]); b.paragraph(essay.introductions.data_based); }
    if (essay.introductions.anecdote) { b.subHeading("Anecdote/Story-Based", [110, 50, 130]); b.paragraph(essay.introductions.anecdote); }
    if (essay.introductions.philosophical) { b.subHeading("Philosophical", [150, 40, 80]); b.paragraph(essay.introductions.philosophical); }
  }
  if (essay.diagram) {
    b.sectionHeading("Essay Structure");
    b.mono(essay.diagram);
  }
  if (essay.body?.length) {
    b.sectionHeading("Essay Body");
    essay.body.forEach((sec) => {
      b.subHeading(`${sec.subheading || ""}${sec.keyword ? ` (${sec.keyword})` : ""}`, [60, 40, 120]);
      if (sec.explanation) b.paragraph(sec.explanation);
      if (sec.example) b.labeledBox("Example", sec.example, [235, 250, 240]);
      if (sec.data) b.labeledBox("Data/Report", sec.data, [235, 244, 255]);
    });
  }
  if (essay.counterview) {
    b.sectionHeading("Counterview / Debate");
    if (essay.counterview.pro) { b.subHeading("Pro", [30, 110, 60]); b.paragraph(essay.counterview.pro); }
    if (essay.counterview.con) { b.subHeading("Con", [160, 50, 50]); b.paragraph(essay.counterview.con); }
    if (essay.counterview.middle_path) { b.subHeading("Middle Path", [110, 50, 130]); b.paragraph(essay.counterview.middle_path); }
  }
  if (essay.way_forward?.length) {
    b.sectionHeading("Way Forward");
    b.list(essay.way_forward);
  }
  if (essay.conclusions) {
    b.sectionHeading("Conclusion Styles");
    if (essay.conclusions.inspirational) { b.subHeading("Inspirational", [60, 60, 140]); b.paragraph(essay.conclusions.inspirational); }
    if (essay.conclusions.constitutional) { b.subHeading("Constitutional/Value-Based", [110, 50, 130]); b.paragraph(essay.conclusions.constitutional); }
    if (essay.conclusions.philosophical) { b.subHeading("Philosophical/Civilizational", [150, 40, 80]); b.paragraph(essay.conclusions.philosophical); }
  }
  if (essay.quotes?.length) {
    b.sectionHeading("Quotation Bank");
    essay.quotes.forEach((q) => b.paragraph(`"${q}"`, { italic: true }));
  }

  b.save(`Clarias_Essay_${safeName(topic)}.pdf`);
}
